/* eslint-disable import/no-cycle */
import {action, decorate, observable, runInAction} from 'mobx';
import * as jwtDecode from 'jwt-decode';
import moment from 'moment';
import i18n from '../i18n/i18n';
import {Api} from '../middleware/api';
import {User} from '../models/user';
import appConfig from '../utils/appConfig';

export class AuthStore {
  user = null;
  token = null;

  constructor(rootStore) {
    this.rootStore = rootStore;
    this.loadUser();
  }

  setCurrentUser({user, token}) {
    this.token = token;
    this.user = User.fromPlainObject(user);
    const decodedJwt = jwtDecode(token);
    if (AuthStore.validateToken(decodedJwt)) {
      this.user.roleIds = decodedJwt.roleIds;
      this.user.setScopes(decodedJwt.scopes);

      this.setRefreshTimeout(decodedJwt);
    } else {
      this.clearUser();
    }
  }

  loadUser() {
    const auth = sessionStorage.getItem('auth');
    if (auth) {
      const authObject = JSON.parse(auth);
      this.setCurrentUser(authObject);
    } else {
      this.guestLogin();
    }
  }

  guestLogin() {
    if (!appConfig.guestLoginToken) {
      return {};
    }
    return Api.auth.guestLogin({token: appConfig.guestLoginToken}).then(({data}) => {
      runInAction(() => {
        this.setCurrentUser(data);
      });
    });
  }

  login(user) {
    return Api.auth.login(user).then(({data}) => {
      runInAction(() => {
        this.setCurrentUser(data);
        this.saveAuthStateIntoSession(data);
      });
    });
  }

  terminalLogin(data) {
    this.setCurrentUser(data);
    this.saveAuthStateIntoSession(data);
  }

  // eslint-disable-next-line class-methods-use-this
  async checkOverrulePermissions({inspectionTask, authenticate, onCheckCompleted}) {
    const {user, token} = await authenticate();
    const decodedJwt = jwtDecode(token);
    const {roleIds} = decodedJwt;
    const {scopes} = decodedJwt;

    if (!scopes.includes('exec:inspection-overrule')) {
      onCheckCompleted({
        success: false,
        message: i18n.t('inspectionTaskPendingModal.taskBox.overrulePermissionMissing'),
        overruleUserId: null,
      });
      return Promise.resolve();
    }

    if (inspectionTask.requiredRoleId) {
      if (!roleIds.includes(inspectionTask.requiredRoleId)) {
        onCheckCompleted({
          success: false,
          message: i18n.t('inspectionTaskPendingModal.taskBox.requiredRoleMissing',
            {requiredRoleId: inspectionTask.requiredRoleId}),
          overruleUserId: null,
        });
        return Promise.resolve();
      }
    }
    onCheckCompleted({
      success: true,
      message: i18n.t('inspectionTaskPendingModal.taskBox.overruleGranted'),
      overruleUserId: user.id,
    });
    return Promise.resolve();
  }

  logout() {
    if (this.user) {
      Api.auth.logout({username: this.user.username}).then(() => {
        this.clearUser();
      });
    }
  }

  clearUser() {
    runInAction(() => {
      sessionStorage.removeItem('auth');
      this.user = null;
      this.token = null;
      this.guestLogin();
    });
  }

  updateAuthToken() {
    if (this.user) {
      Api.auth.refreshToken().then(({data}) => {
        runInAction(() => {
          this.setCurrentUser(data);
          this.saveAuthStateIntoSession(data);
        });
      });
    }
  }

  static validateToken(token) {
    if (token && token.exp) {
      return moment(token.exp * 1000).isAfter(new Date());
    }
    return !!token;
  }

  setRefreshTimeout(decodedToken) {
    if (!decodedToken.exp) {
      return;
    }
    let timeoutMs = moment(moment(decodedToken.exp * 1000)).diff(new Date());
    timeoutMs -= 10000; // Remove 10 seconds to ensure refresh happens in time.
    const notThis = this;
    setTimeout(() => notThis.updateAuthToken(), Math.max(timeoutMs, 0));
  }

  getRequestHeaders() {
    if (!this.token) {
      return {};
    }
    return {
      Authorization: `Bearer ${this.token}`,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  saveAuthStateIntoSession(data) {
    sessionStorage.setItem('auth', JSON.stringify(data));
  }
}

decorate(AuthStore, {
  user: observable,
  setCurrentUser: action,
  login: action,
  terminalLogin: action,
  checkOverrulePermissions: action,
  logout: action,
  updateAuthToken: action,
  requestHeaders: action,
  validateToken: action,
});
